<template>
    <div style="text-align: left;" class="mx-2 mx-md-0">
        <!-- Tab Navigation -->
        <div class="d-flex justify-content-between align-items-center mb-2 py-2">
            <b-tabs v-model="activeTab" content-class="mt-3" fill style="flex: 1;">
                <b-tab :active="activeTab === 0" @click="switchTab(0)">
                    <template #title>
                        <b-icon-truck class="mr-1" />
                        Assign Pickup Truck
                        <b-badge variant="warning" pill class="ml-1">{{ pickupTabItemCount }}</b-badge>
                    </template>
                </b-tab>
                <b-tab :active="activeTab === 1" @click="switchTab(1)">
                    <template #title>
                        <b-icon-truck class="mr-1" />
                        Assign Delivery Truck
                        <b-badge variant="success" pill class="ml-1">{{ deliveryTabItemCount }}</b-badge>
                    </template>
                </b-tab>
            </b-tabs>

            <!-- Mobile Filter Toggle -->
            <b-button
                v-if="isMobile"
                size="sm"
                variant="outline-primary"
                @click="filtersVisible = !filtersVisible"
                class="ml-2"
            >
                <b-icon-funnel-fill class="mr-1" />
                {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
            </b-button>
        </div>

        <b-collapse :visible="!isMobile || filtersVisible" style="width: 100%;">
            <div style="margin-bottom: 5px;">
                <!-- ==================== COMMON FILTERS (Both Tabs) ==================== -->
                <b-alert variant="secondary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;">
                    <div style="display: inline-block; vertical-align: top;">
                        <div>SELECT DATE RANGE</div>
                        <b-input
                            id="dates"
                            size="lg"
                            variant="dark"
                            :placeholder="[query.startDate, query.endDate].join(' - ')"
                            style="display: inline-block; width: 220px; font-size: 14px; font-weight: bold;"
                            class="responsive-input"
                            autocomplete="off"
                        ></b-input>
                        <b-button size="sm" variant="dark" @click="search_dates" class="mt-1 mt-md-0">SEARCH</b-button>
                    </div>

                    <div style="display: inline-block; vertical-align: top;" v-if="query.startDate && query.endDate">
                        <div>ORDER TYPE</div>
                        <b-form-select size="lg" :options="order_types" v-model="order_type"
                            style="width: 110px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- TYPE --</b-form-select-option>
                                <b-form-select-option :value="null">ALL</b-form-select-option>
                            </template>
                        </b-form-select>
                    </div>

                    <div style="display: inline-block; vertical-align: top;" v-if="query.startDate && query.endDate">
                        <div>ITEM STATUS</div>
                        <b-form-select size="lg" :options="limitedStatusOptions" v-model="state"
                            style="width: 110px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- STATUS --</b-form-select-option>
                                <b-form-select-option :value="null">ALL</b-form-select-option>
                            </template>
                        </b-form-select>
                    </div>
                </b-alert>

                <!-- Product Search - Common -->
                <b-alert variant="info" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                        v-if="query.startDate && query.endDate && items.length > 0">
                    <div>SEARCH PRODUCT</div>
                    <b-input
                        size="lg"
                        :placeholder="'SKU or product name'"
                        style="width: 200px; font-size: 14px;"
                        v-model="product_search_filter"
                        @input="applyTextFilters"
                        debounce="300"
                    ></b-input>
                </b-alert>

                <!-- Invoice Search - Common -->
                <b-alert variant="info" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                        v-if="query.startDate && query.endDate && items.length > 0">
                    <div>SEARCH INVOICE</div>
                    <b-input
                        size="lg"
                        :placeholder="'Invoice #'"
                        style="width: 150px; font-size: 14px;"
                        v-model="invoice_number_filter"
                        @input="applyTextFilters"
                        debounce="300"
                    ></b-input>
                </b-alert>

                <!-- ==================== PICKUP TAB FILTERS ==================== -->
                <template v-if="activeTab === 0">
                    <!-- Transfer Status Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>TRANSFER STATUS</div>
                        <b-form-select 
                            size="lg" 
                            :options="truckStatusOptions" 
                            v-model="truck_assignment_filter"
                            style="width: 150px; font-size: 14px; font-weight: bold;"
                        >
                        </b-form-select>
                    </b-alert>

                    <!-- Transfer Truck Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>TRANSFER TRUCK</div>
                        <b-form-select size="lg" :options="trucks" v-model="pick_truck_filter"
                            style="width: 180px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- SELECT TRUCK --</b-form-select-option>
                                <b-form-select-option :value="null">ALL TRUCKS</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-alert>

                    <!-- Pick From Location Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>PICK FROM LOCATION</div>
                        <b-form-select size="lg" :options="packet.locations" v-model="location_filter"
                            style="width: 180px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- ITEM LOCATION --</b-form-select-option>
                                <b-form-select-option :value="null">ALL LOCATIONS</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-alert>

                    <!-- Drop Location Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>DROP LOCATION</div>
                        <b-form-select size="lg" :options="packet.locations" v-model="drop_location_filter"
                            style="width: 180px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- DROP LOCATION --</b-form-select-option>
                                <b-form-select-option :value="null">ALL LOCATIONS</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-alert>

                    <!-- Bulk Assign Pickup Truck Button -->
                    <b-alert variant="info" show style="display: inline-block; vertical-align: top; margin-left: 5px; margin-bottom: 5px;" 
                            v-if="selectedItems.length > 0">
                        <div>BULK ASSIGN PICKUP TRUCK</div>
                        <b-button
                            size="sm"
                            variant="warning"
                            @click="bulkAssignPickupTruck"
                            :disabled="!hasPickupAssignableItems"
                        >
                            <b-icon-truck></b-icon-truck> Assign Pickup Truck ({{ selectedPickupEligibleCount }})
                        </b-button>
                    </b-alert>
                </template>

                <!-- ==================== DELIVERY TAB FILTERS ==================== -->
                <template v-if="activeTab === 1">
                    <!-- Delivery Status Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>DELIVERY STATUS</div>
                        <b-form-select 
                            size="lg" 
                            :options="deliveryStatusOptions" 
                            v-model="delivery_status_filter"
                            style="width: 150px; font-size: 14px; font-weight: bold;"
                        >
                        </b-form-select>
                    </b-alert>

                    <!-- Delivery Truck Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>DELIVERY TRUCK</div>
                        <b-form-select size="lg" :options="trucks" v-model="delivery_truck_filter"
                            style="width: 180px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- SELECT TRUCK --</b-form-select-option>
                                <b-form-select-option :value="null">ALL TRUCKS</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-alert>

                    <!-- Delivery Location Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>DELIVERY LOCATION</div>
                        <b-form-select size="lg" :options="deliveryLocationOptions" v-model="delivery_location_filter"
                            style="width: 180px; font-size: 14px; font-weight: bold;">
                            <template #first>
                                <b-form-select-option :value="null" disabled>-- DELIVERY LOCATION --</b-form-select-option>
                                <b-form-select-option :value="null">ALL</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-alert>

                    <!-- Truck Type Filter -->
                    <b-alert variant="primary" show style="display: inline-block; vertical-align: top; margin-right: 5px; margin-bottom: 5px;" 
                            v-if="query.startDate && query.endDate && items.length > 0">
                        <div>TRUCK TYPE</div>
                        <b-form-select 
                            size="lg" 
                            :options="truckTypeOptions" 
                            v-model="truck_type_filter"
                            style="width: 180px; font-size: 14px; font-weight: bold;"
                        >
                        </b-form-select>
                    </b-alert>

                    <!-- Bulk Assign Delivery Truck Button -->
                    <b-alert variant="info" show style="display: inline-block; vertical-align: top; margin-left: 5px; margin-bottom: 5px;" 
                            v-if="selectedItems.length > 0">
                        <div>BULK ASSIGN DELIVERY TRUCK</div>
                        <b-button
                            size="sm"
                            variant="success"
                            @click="bulkAssignDeliveryTruck"
                            :disabled="!hasDeliveryAssignableItems"
                        >
                            <b-icon-truck></b-icon-truck> Assign Delivery Truck ({{ selectedDeliveryEligibleCount }})
                        </b-button>
                    </b-alert>
                </template>

                <!-- Clear Filters Button - Common -->
                <b-alert variant="warning" show style="display: inline-block; vertical-align: top; margin-bottom: 5px;" 
                        v-if="hasActiveTextFilters">
                    <div>CLEAR FILTERS</div>
                    <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="clearTextFilters"
                        style="font-size: 14px;"
                    >
                        Clear
                    </b-button>
                </b-alert>
            </div>
        </b-collapse>

        <!-- ==================== TABLE SECTION ==================== -->
        <b-card no-body>
            <div style="min-height: 500px; max-height: 1000px; overflow-y: scroll; height: 100%;">
                <div style="border: 1px solid gray; padding: 10px;">
                    <div style="width: 100%; height: 100%;" v-if="items_being_loaded">
                        <loading :active="true" :can-cancel="false" :is-full-page="false">
                        </loading>
                    </div>

                    <div v-else>
                        <div v-if="filteredTabItems.length > 0" style="position: relative; overflow-x: auto;">
                            <div style="display: inline-block; vertical-align: top; margin: 5px;">
                                <h5 style="z-index:100; font-family: courier; font-weight: bold;">
                                    {{ activeTab === 0 ? 'PICKUP TRUCK ASSIGNMENT' : 'DELIVERY TRUCK ASSIGNMENT' }} 
                                    FOR {{ query.startDate }} TO {{ query.endDate }}
                                </h5>
                            </div>

                            <div style="display: inline-block; vertical-align: top; margin: 5px;">
                                <download-csv class="btn" :data="get_csv_data()"
                                    style="border: 1px solid black; width: 180px; background: black; color: white; "
                                    :name="get_csv_filename()">
                                    DOWNLOAD EXCEL
                                </download-csv>
                                <b-button
                                    size="md"
                                    variant="success"
                                    class="ml-2"
                                    :disabled="!showSaveButton"
                                    @click="save_selected_pickup_items"
                                >
                                    {{ saveButtonText }}
                                </b-button>
                            </div>

                            <!-- Common Truck Assign for selected (Pickup Tab) -->
                            <div v-if="activeTab === 0 && selectedItems.length > 1" style="position: sticky; width:100%; top: 0; background: white; z-index: 100; padding: 5px; border-bottom: 1px solid #ccc;">
                                Assign pickup truck to all selected:
                                <b-form-select
                                    size="sm"
                                    style="width: 150px; display: inline-block; margin-left: 10px;"
                                    :options="trucks"
                                    v-model="commonTruck"
                                    @change="update_selected_trucks_stats()"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null">Select Truck</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </div>

                            <!-- ==================== PICKUP TAB TABLE ==================== -->
                            <table v-if="activeTab === 0" style="width: 100%;">
                                <thead>
                                    <tr style="background-color: black; color: white;">
                                        <th class="ipt_tdr" style="min-width: 30px; text-align: center; background-color: black; color: white;">
                                            <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('invoice_id')" style="background-color: black; color: white;">
                                            INVOICE #
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('product_name')" style="min-width: 200px; background-color: black; color: white;">
                                            PRODUCT NAME
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('product_sku')" style="background-color: black; color: white;">
                                            PRODUCT SKU
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('order_type')" style="background-color: black; color: white;">
                                            ORDER TYPE
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;">
                                            QTY
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;" @click="sortby('state')">
                                            STATUS
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;" @click="sortby('')">
                                            DELIVERY DATE
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('locsub')" style="background-color: black; color: white;">
                                            PICK FROM
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('pickup_truck', packet.trucksdict)" style="background-color: black; color: white;">
                                            TRANSFER TRUCK
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;">
                                            TRANSFER STATUS
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('drop_locsub')" style="background-color: black; color: white;">
                                            DROP LOCATION
                                        </th>
                                        <th class="ipt_tdr" hidden-print style="background-color: black; color: white;">
                                            COMMENTS
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item, idx in filteredTabItems" :key="item.itemid" v-if="allowed(item)">
                                        <td class="ipt_tdr" style="text-align: center;">
                                            <input
                                                type="checkbox"
                                                v-model="selectedItems"
                                                :value="item"
                                                :disabled="!isSelectable(item)"
                                            >
                                        </td>
                                        <td class="ipt_tdr" style="font-size: 12px;">
                                            {{ getDisplayInvoiceId(item) }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ (item.product_name || item.product_sku) }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ (item.product_sku || '').toUpperCase() }}
                                        </td>
                                        <td class="ipt_tdr" :style="{ 'background-color': get_order_type_color(item.order_type) }">
                                            {{ getDisplayOrderType(item) }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ item.quantity }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ limitedStatesDict[item.state] || states_dict[item.state] }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ item.pull_date }}
                                        </td>
                                        <td class="ipt_tdr">
                                            <b-form-select size="sm" style="font-size: 12px; width: 200px;"
                                                :value="getDisplayLocsub(item)"
                                                @input="updateLocsub(item, $event)"
                                                :disabled="isPickLocationDisabled(item)">
                                                <b-form-select-option
                                                    v-for="locsub, lidx in getAvailableLocsubs(item)"
                                                    :value="locsub.value"
                                                    v-bind:key="lidx"
                                                    :style="isOutOfStock(item, locsub) ? 'color: red; font-style: italic;' : ''"
                                                >
                                                    {{ all_loc_inventory_dict[concater([item.product_sku, locsub.location_id, locsub.sublocation_id])] || 0 }}
                                                    - {{ locsub.location }} | {{ locsub.sublocation }}
                                                    {{ isOutOfStock(item, locsub) ? '(Out of Stock)' : '' }}
                                                </b-form-select-option>
                                            </b-form-select>
                                        </td>
                                        <td class="ipt_tdr">
                                            <b-form-select 
                                                size="sm" 
                                                style="width: 130px;" 
                                                :options="trucks"
                                                v-model="item.pickup_truck"
                                                :disabled="isDisabled(item) || item.state == 5"
                                                @input="onPickupTruckChange(item)"
                                            >
                                            </b-form-select>
                                        </td>
                                        <td class="ipt_tdr">
                                            <span :class="getTruckStatusClass(item)">
                                                {{ getTruckStatus(item) }}
                                            </span>
                                        </td>
                                        <td class="ipt_tdr">
                                            <b-form-select
                                                v-model="item.ship_locsub"
                                                style="width: 200px; font-size: 11px;"
                                                :disabled="isDisabled(item)"
                                                @input="onShipLocsubChange(item)"
                                            >
                                                <b-form-select-option
                                                    v-for="opt in buildStageLocsubOptions(item)"
                                                    :key="opt.value"
                                                    :value="opt.value"
                                                >
                                                    {{ opt.label }}
                                                </b-form-select-option>
                                            </b-form-select>
                                        </td>
                                        <td class="ipt_tdr hidden-print">
                                            <span v-if="comments_dict[getCommentKey(item)]">
                                                <button style="border: 1px solid black; color: red; font-weight: bold;"
                                                    @click="show_comments(item)" :disabled="isDisabled(item)">CMNT ({{ comments_dict[getCommentKey(item)]}})
                                                </button>
                                            </span>
                                            <span v-else>
                                                <button @click="show_comments(item)" :disabled="isDisabled(item)">CMNT
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- ==================== DELIVERY TAB TABLE ==================== -->
                            <table v-if="activeTab === 1" style="width: 100%;">
                                <thead>
                                    <tr style="background-color: black; color: white;">
                                        <th class="ipt_tdr" style="min-width: 30px; text-align: center; background-color: black; color: white;">
                                            <input type="checkbox" v-model="selectAll" @change="toggleSelectAll">
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('invoice_id')" style="background-color: black; color: white;">
                                            INVOICE #
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('product_name')" style="min-width: 200px; background-color: black; color: white;">
                                            PRODUCT NAME
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('product_sku')" style="background-color: black; color: white;">
                                            PRODUCT SKU
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('order_type')" style="background-color: black; color: white;">
                                            ORDER TYPE
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;">
                                            QTY
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;" @click="sortby('state')">
                                            STATUS
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;" @click="sortby('')">
                                            DELIVERY DATE
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('drop_locsub')" style="background-color: black; color: white;">
                                            DROP LOCATION
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('truck', packet.trucksdict)" style="background-color: black; color: white;">
                                            DELIVERY TRUCK
                                        </th>
                                        <th class="ipt_tdr" style="background-color: black; color: white;">
                                            DELIVERY STATUS
                                        </th>
                                        <th class="ipt_tdr" @click="sortby('drop_locsub')" style="background-color: black; color: white;">
                                            DELIVERY LOCATION
                                        </th>
                                        <th class="ipt_tdr" hidden-print style="background-color: black; color: white;">
                                            COMMENTS
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item, idx in filteredTabItems" :key="item.itemid" v-if="allowed(item)">
                                        <td class="ipt_tdr" style="text-align: center;">
                                            <input
                                                type="checkbox"
                                                v-model="selectedItems"
                                                :value="item"
                                                :disabled="!isSelectable(item)"
                                            >
                                        </td>
                                        <td class="ipt_tdr" style="font-size: 12px;">
                                            {{ getDisplayInvoiceId(item) }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ (item.product_name || item.product_sku) }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ (item.product_sku || '').toUpperCase() }}
                                        </td>
                                        <td class="ipt_tdr" :style="{ 'background-color': get_order_type_color(item.order_type) }">
                                            {{ getDisplayOrderType(item) }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ item.quantity }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ limitedStatesDict[item.state] || states_dict[item.state] }}
                                        </td>
                                        <td class="ipt_tdr">
                                            {{ item.pull_date }}
                                        </td>
                                        <td class="ipt_tdr">
                                            <b-form-select
                                                v-model="item.ship_locsub"
                                                style="width: 200px; font-size: 11px;"
                                                :disabled="isDisabled(item)"
                                                @input="onShipLocsubChange(item)"
                                            >
                                                <b-form-select-option
                                                    v-for="opt in buildStageLocsubOptions(item)"
                                                    :key="opt.value"
                                                    :value="opt.value"
                                                >
                                                    {{ opt.label }}
                                                </b-form-select-option>
                                            </b-form-select>
                                        </td>
                                        <td class="ipt_tdr">
                                            <b-form-select 
                                                size="sm" 
                                                style="width: 130px;" 
                                                :options="trucks"
                                                v-model="item.truck"
                                                :disabled="isDeliveryTruckDisabled(item)"
                                                @input="onDeliveryTruckChange(item)"
                                            >
                                            </b-form-select>
                                        </td>
                                        <td class="ipt_tdr">
                                            <span :class="getDeliveryStatusClass(item)">
                                                {{ getDeliveryStatus(item) }}
                                            </span>
                                        </td>
                                        <td class="ipt_tdr">
                                            <b-form-select
                                                v-model="item.drop_locsub"
                                                style="width: 200px; font-size: 11px;"
                                                :disabled="isDeliveryLocationDisabled(item)"
                                                @input="onDropLocsubChange(item)"
                                            >
                                                <b-form-select-option
                                                    v-for="opt in getDeliveryLocationOptions(item)"
                                                    :key="opt.value"
                                                    :value="opt.value"
                                                >
                                                    {{ opt.label }}
                                                </b-form-select-option>
                                            </b-form-select>
                                        </td>
                                        <td class="ipt_tdr hidden-print">
                                            <span v-if="comments_dict[getCommentKey(item)]">
                                                <button style="border: 1px solid black; color: red; font-weight: bold;"
                                                    @click="show_comments(item)" :disabled="isDisabled(item)">CMNT ({{ comments_dict[getCommentKey(item)]}})
                                                </button>
                                            </span>
                                            <span v-else>
                                                <button @click="show_comments(item)" :disabled="isDisabled(item)">CMNT
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <!-- Empty State -->
                            <div v-if="filteredTabItems.length === 0" class="text-center py-5">
                                <b-icon icon="inbox" font-scale="3" class="text-muted mb-3"></b-icon>
                                <h5 class="text-muted">
                                    {{ activeTab === 0 ? 'No items requiring pickup truck assignment' : 'No items requiring delivery truck assignment' }}
                                </h5>
                                <p class="text-muted">Try adjusting your filters or date range.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-card>

        <!-- Bulk Assignment Modal -->
        <b-modal id="modal-bulk-assign-truck" size="sm" hide-footer :title="bulkAssignmentTitle">
            <div v-if="bulkAssignmentType === 'pickup'">
                <p>Assign pickup truck to <strong>{{ selectedPickupEligibleCount }}</strong> selected item(s):</p>
                <b-form-select
                    v-model="bulkSelectedTruck"
                    :options="trucks"
                    size="sm"
                    style="width: 100%; margin-bottom: 15px;"
                >
                    <template #first>
                        <b-form-select-option :value="null">-- Select Pickup Truck --</b-form-select-option>
                    </template>
                </b-form-select>
            </div>
            
            <div v-else-if="bulkAssignmentType === 'delivery'">
                <p>Assign delivery truck to <strong>{{ selectedDeliveryEligibleCount }}</strong> selected item(s):</p>
                <b-form-select
                    v-model="bulkSelectedTruck"
                    :options="trucks"
                    size="sm"
                    style="width: 100%; margin-bottom: 15px;"
                >
                    <template #first>
                        <b-form-select-option :value="null">-- Select Delivery Truck --</b-form-select-option>
                    </template>
                </b-form-select>
            </div>
            
            <div class="text-right">
                <b-button
                    size="sm"
                    variant="secondary"
                    @click="$bvModal.hide('modal-bulk-assign-truck')"
                    style="margin-right: 5px;"
                >
                    Cancel
                </b-button>
                <b-button
                    size="sm"
                    :variant="bulkAssignmentType === 'pickup' ? 'warning' : 'success'"
                    @click="confirmBulkAssignment"
                    :disabled="!bulkSelectedTruck"
                >
                    Assign
                </b-button>
            </div>
        </b-modal>

        <b-modal id="modal-comments-process-pickups" scrollable size="md" hide-footer
            :title="'COMMENTS FOR (' + item_comments_invoice_id + ')'">
            <ItemComments
                :viewdata="{ invoice_id: item_comments_invoice_id, itemid: item_comments_itemid, username: packet.userDetails.username }" />
        </b-modal>
    </div>
</template>

<script>
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import Requestor from '@/utilities/Requestor';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import ItemComments from '@/components/importables/ItemComments'
import mappings from '@/utilities/mappings'
import { BIconFunnelFill, BIconTruck } from "bootstrap-vue";

export default {
    name: "AdminToolsProcessDeliveries",
    components: { flatPickr, Loading, ItemComments, BIconTruck },
    data: function () {
        return {
            packet: {},
            all_loc_inventory_dict: {},
            states_dict: mappings.states_dict || [],
            limitedStatesDict: {},
            state: null,
            truck_type_filter: 'all',
            items: [],
            originalValues: {},
            filteredItems: [],
            query: {
                startDate: moment().format('YYYY-MM-DD'),
                endDate: moment().format('YYYY-MM-DD')
            },
            location_filter: null,
            drop_location_filter: null,
            pick_truck_filter: null,
            delivery_truck_filter: null,
            manual_location_id: null,
            manual_truck_id: null,
            trucks: [],
            trucks_dict: {},
            trucks_being_loaded: true,
            items_being_loaded: false,
            trucks_stats: {},
            default_locsubs: [],
            drop_locsub: null,
            ship_locsub: null,
            delivery_location_filter: null,
            delivery_status_filter: 'all',
            comments_dict: {},
            item_comments_invoice_id: null,
            item_comments_itemid: null,
            sort_asc: true,
            default_location_id: null,
            order_types: [
                { value: 'home_delivery', text: 'HOME DELIVERY' },
                { value: 'store_pickup', text: 'STORE PICKUP' },
                { value: 'store_transfer_order', text: 'STORE TRANSFER' },
            ],
            order_type: null,
            selectAll: false,
            selectedItems: [],
            commonTruck: null,
            originalTrucks: {},
            filtersVisible: true,
            isMobile: false,
            product_search_filter: '',
            invoice_number_filter: '',
            truck_assignment_filter: 'all',
            forceUpdateFlag: false,
            // Bulk assignment variables
            bulkAssignmentType: null,
            bulkSelectedTruck: null,
            bulkAssignmentTitle: '',
            // Show same location items toggle
            showSameLocationItems: false,
            // Active tab: 0 = Pickup Truck, 1 = Delivery Truck
            activeTab: 0
        }
    },
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth < 768;
        },
        switchTab(tabIndex) {
            this.activeTab = tabIndex;
            this.selectedItems = [];
            this.selectAll = false;
            this.commonTruck = null;
            this.$nextTick(() => {
                this.filterItems();
            });
        },
        getDisplayInvoiceId: function(item) {
            if (item.item_type === 'transfer') {
                return `TRANSFER-${item.transfer_order_id || 'N/A'}`;
            }
            return item.invoice_id || 'N/A';
        },
        getDisplayOrderType: function(item) {
            if (item.item_type === 'transfer') {
                return 'STORE TRANSFER ORDER';
            }
            return (item.order_type || '').toUpperCase();
        },
        getCommentKey: function(item) {
            if (item.item_type === 'transfer') {
                return `TRANSFER_${item.transfer_order_id}_${item.itemid}`;
            }
            return `${item.invoice_id}|${item.itemid}`;
        },
        getDisplayLocsub: function(item) {
            return item.locsub || item.original_locsub;
        },
        updateLocsub: function(item, newValue) {
            this.$set(item, 'original_locsub', newValue);
            this.onFieldChange(item);
        },
        isSelectable: function(item) {
            if (item.item_type === 'transfer') {
                return true;
            }
            return Number(item.state) === 0 || 
                   Number(item.state) === 2 || 
                    Number(item.state) === 9 || 
                   (Number(item.state) === 30 && Number(item.location_id) !== 46);
        },
        
        isDeliveryLocationDisabled: function(item) {
            return !(Number(item.state) === 30 || 
                    Number(item.state) === 9);
        },
        
        getDeliveryLocationOptions: function(item) {
            let opts = [];
            let allLocsubsdict = this.packet.locsubsdict || {};

            if (item.drop_locsub && allLocsubsdict[item.drop_locsub]) {
                opts.push({
                    value: item.drop_locsub,
                    label: allLocsubsdict[item.drop_locsub]
                });
            }

            if (item.order_type === "store_pickup") {
                let storePickupLocsub = "47|162";
                if (allLocsubsdict[storePickupLocsub] && !opts.find(o => o.value === storePickupLocsub)) {
                    opts.push({
                        value: storePickupLocsub,
                        label: allLocsubsdict[storePickupLocsub]
                    });
                }
            }

            if (item.sold_locsub && allLocsubsdict[item.sold_locsub] && 
                !opts.find(o => o.value === item.sold_locsub)) {
                opts.push({
                    value: item.sold_locsub,
                    label: allLocsubsdict[item.sold_locsub]
                });
            }

            if (item.item_type === 'transfer' && item.transfer_to_location_id) {
                const transferLocsub = Object.keys(allLocsubsdict).find(key => {
                    const locationId = key.split('|')[0];
                    return Number(locationId) === Number(item.transfer_to_location_id);
                });
                
                if (transferLocsub && allLocsubsdict[transferLocsub] && 
                    !opts.find(o => o.value === transferLocsub)) {
                    opts.push({
                        value: transferLocsub,
                        label: allLocsubsdict[transferLocsub]
                    });
                }
            }

            let defaultLocsub = "47|131";
            if (allLocsubsdict[defaultLocsub] && !opts.find(o => o.value === defaultLocsub)) {
                opts.push({
                    value: defaultLocsub,
                    label: allLocsubsdict[defaultLocsub]
                });
            }

            if (this.packet.locations) {
                this.packet.locations.forEach(loc => {
                    if (loc.store_id) {
                        const storeLocsub = Object.keys(allLocsubsdict).find(key => {
                            const locationId = key.split('|')[0];
                            return Number(locationId) === Number(loc.id);
                        });
                        
                        if (storeLocsub && allLocsubsdict[storeLocsub] && 
                            !opts.find(o => o.value === storeLocsub)) {
                            opts.push({
                                value: storeLocsub,
                                label: allLocsubsdict[storeLocsub]
                            });
                        }
                    }
                });
            }

            return opts.filter((option, index, self) =>
                index === self.findIndex((t) => t.value === option.value)
            );
        },
        
        isDeliveryTruckDisabled: function(item) {
            const areLocationsSame = this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub);
            if (areLocationsSame) {
                return true;
            }
            return !(Number(item.state) === 9);
        },
        
        areLocationsSameMainLocation: function(dropLocsub, shipLocsub) {
            if (!dropLocsub || !shipLocsub) return false;
            const dropParts = dropLocsub.toString().split('|');
            const shipParts = shipLocsub.toString().split('|');
            if (dropParts.length < 2 || shipParts.length < 2) return false;
            return dropParts[0] === shipParts[0];
        },

        areLocationsNotSameMainLocation: function(dropLocsub, shipLocsub) {
            if (!dropLocsub || !shipLocsub) return false;
            const dropParts = dropLocsub.toString().split('|');
            const shipParts = shipLocsub.toString().split('|');
            if (dropParts.length < 2 || shipParts.length < 2) return false;
            return dropParts[0] !== shipParts[0];
        },
        
        getTruckStatus: function(item) {
            if (!item.pickup_truck || item.pickup_truck === null || item.pickup_truck === '') {
                return 'NOT ASSIGNED';
            }
            if (Number(item.state) === 30) {
                return 'COMPLETED';
            }
            if (Number(item.state) === 13 || Number(item.state) === 9) {
                if (this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub)) {
                    return 'ASSIGNED';
                } else {
                    return 'COMPLETED';
                }
            }
            if (Number(item.state) === 3) {
                return 'PICKED';
            }
            return 'ASSIGNED';
        },
        
        getTruckStatusClass: function(item) {
            const status = this.getTruckStatus(item);
            switch(status) {
                case 'COMPLETED': return 'truck-status-delivered';
                case 'PICKED': return 'truck-status-picked';
                case 'DROPPED': return 'truck-status-dropped';
                case 'ASSIGNED': return 'truck-status-assigned';
                case 'NOT ASSIGNED':
                default: return 'truck-status-not-assigned';
            }
        },
        
        getDeliveryStatus: function(item) {
            const areLocationsSame = this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub);
            if (areLocationsSame) {
                const transferStatus = this.getTruckStatus(item);
                switch(transferStatus) {
                    case 'COMPLETED': return 'COMPLETED';
                    case 'PICKED': return 'PICKED';
                    case 'DISPATCHED': return 'DISPATCHED';
                    case 'DROPPED': return 'COMPLETED';
                    case 'ASSIGNED': return 'ASSIGNED';
                    case 'NOT ASSIGNED':
                    default: return 'NOT ASSIGNED';
                }
            }
            if (!item.truck || item.truck === null || item.truck === '') {
                return 'NOT ASSIGNED';
            }
            if (item.truck && (Number(item.state) === 3 || Number(item.state) === 30)) {
                return 'ASSIGNED';
            }
            if (Number(item.state) === 10) return 'DISPATCHED';
            if (Number(item.state) === 11) return 'PICKED';
            if (Number(item.state) === 13) return 'DROPPED';
            if (Number(item.state) === 13 || Number(item.state) === 4) return 'COMPLETED';
            return 'ASSIGNED';
        },
        
        getDeliveryStatusClass: function(item) {
            const status = this.getDeliveryStatus(item);
            switch(status) {
                case 'COMPLETED': return 'delivery-status-delivered';
                case 'PICKED': return 'delivery-status-in-picked';
                case 'DROPPED': return 'delivery-status-dropped';
                case 'DISPATCHED': return 'delivery-status-dispatched';
                case 'ASSIGNED': return 'delivery-status-assigned';
                case 'NOT ASSIGNED':
                default: return 'delivery-status-not-assigned';
            }
        },

        shouldShowDeliveryTruck: function(item) {
            return !this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub);
        },

        checkAndSyncDeliveryStatus: function(item) {
            if (item.ship_locsub && item.drop_locsub) {
                const areLocationsSame = this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub);
                if (areLocationsSame) {
                    if (item.truck && item.truck !== '') {
                        item.truck = null;
                        this.$set(item, 'truck_modified', true);
                    }
                    this.$nextTick(() => {
                        this.$forceUpdate();
                    });
                }
            }
        },

        // Determine if item should show in Pickup tab
        shouldShowInPickupTab(item) {
            const areLocationsSame = this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub);
            const transferStatus = this.getTruckStatus(item);
            if (areLocationsSame && transferStatus === 'COMPLETED') {
                return false;
            }
            return true;
        },
        
        // Determine if item should show in Delivery tab
        shouldShowInDeliveryTab(item) {
            if (this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub)) {
                return false;
            }
            const deliveryRelevantStates = [9, 10, 11, 13, 30];
            const state = Number(item.state);
            return deliveryRelevantStates.includes(state) || (item.truck && item.truck !== '');
        },

        filterItems: function () {
            if (!Array.isArray(this.items)) {
                this.filteredItems = [];
                return;
            }

            this.filteredItems = this.items.filter(item => {
                if (!item) return false;

                const orderTypeMatch = 
                    this.order_type === null ||
                    (item.item_type === 'transfer' && this.order_type === 'store_transfer_order') ||
                    item.order_type === this.order_type;

                const stateMatch = 
                    this.state === null ||
                    Number(item.state) === Number(this.state);

                const productSearch = !this.product_search_filter || 
                    (item.product_sku && item.product_sku.toLowerCase()
                        .includes(this.product_search_filter.toLowerCase())) ||
                    (item.product_name && item.product_name.toLowerCase()
                        .includes(this.product_search_filter.toLowerCase()));

                const invoiceMatch = !this.invoice_number_filter || 
                    this.getDisplayInvoiceId(item).toString()
                        .toLowerCase()
                        .includes(this.invoice_number_filter.toLowerCase());

                const truckMatch = this.pick_truck_filter === null ||
                    (this.pick_truck_filter && item.pickup_truck === this.pick_truck_filter);

                const deliveryTruckMatch = this.delivery_truck_filter === null ||
                    (this.delivery_truck_filter && item.truck === this.delivery_truck_filter);

                const deliveryLocationMatch = this.delivery_location_filter === null ||
                    (item.drop_locsub && parseInt(item.drop_locsub.split("|")[0], 10) === this.delivery_location_filter);

                let truckAssignmentMatch = true;
                const truckStatus = this.getTruckStatus(item);
                
                if (this.truck_assignment_filter === 'assigned') {
                    truckAssignmentMatch = truckStatus === 'ASSIGNED' || truckStatus === 'PICKED' || truckStatus === 'DROPPED';
                } else if (this.truck_assignment_filter === 'not_assigned') {
                    truckAssignmentMatch = truckStatus === 'NOT ASSIGNED';
                } else if (this.truck_assignment_filter === 'completed') {
                    truckAssignmentMatch = truckStatus === 'COMPLETED';
                } else if (this.truck_assignment_filter === 'picked') {
                    truckAssignmentMatch = truckStatus === 'PICKED';
                } else if (this.truck_assignment_filter === 'dropped') {
                    truckAssignmentMatch = truckStatus === 'DROPPED';
                }

                let deliveryStatusMatch = true;
                const deliveryStatus = this.getDeliveryStatus(item);
                
                if (this.delivery_status_filter === 'assigned') {
                    deliveryStatusMatch = deliveryStatus === 'ASSIGNED';
                } else if (this.delivery_status_filter === 'not_assigned') {
                    deliveryStatusMatch = deliveryStatus === 'NOT ASSIGNED';
                } else if (this.delivery_status_filter === 'dropped') {
                    deliveryStatusMatch = deliveryStatus === 'DROPPED';
                } else if (this.delivery_status_filter === 'picked') {
                    deliveryStatusMatch = deliveryStatus === 'PICKED';
                } else if (this.delivery_status_filter === 'completed') {
                    deliveryStatusMatch = deliveryStatus === 'COMPLETED';
                }

                let truckTypeMatch = true;
                if (this.truck_type_filter === 'pickup') {
                    truckTypeMatch = this.areLocationsNotSameMainLocation(item.drop_locsub, item.ship_locsub);
                } else if (this.truck_type_filter === 'delivery') {
                    truckTypeMatch = this.areLocationsSameMainLocation(item.drop_locsub, item.ship_locsub);
                }

                return orderTypeMatch && stateMatch && productSearch && invoiceMatch && 
                       truckMatch && deliveryTruckMatch && deliveryLocationMatch && 
                       truckAssignmentMatch && truckTypeMatch && deliveryStatusMatch;
            });
        },

        applyTextFilters() {
            this.filterItems();
        },
        
        clearTextFilters() {
            this.product_search_filter = '';
            this.invoice_number_filter = '';
            this.filterItems();
        },

        buildLocsubOptions: function(item) {
            let opts = [];
            if (item.item_type === 'transfer') {
                let defaultLocsub = "47|131";
                opts.push({
                    value: defaultLocsub,
                    label: this.packet.locsubsdict[defaultLocsub] || "Default Location"
                });
                if (item.drop_locsub && !opts.find(o => o.value === item.drop_locsub)) {
                    opts.push({
                        value: item.drop_locsub,
                        label: this.packet.locsubsdict[item.drop_locsub] || item.drop_locsub
                    });
                }
                return opts;
            }
            let defaultLocsub = item.order_type === "store_pickup" ? "47|162" : "47|131";
            opts.push({
                value: defaultLocsub,
                label: this.packet.locsubsdict[defaultLocsub] || "Default Location"
            });
            if (item.sold_locsub && !opts.find(o => o.value === item.sold_locsub)) {
                opts.push({
                    value: item.sold_locsub,
                    label: this.packet.locsubsdict[item.sold_locsub] || item.sold_locsub
                });
            }
            if (item.drop_locsub && !opts.find(o => o.value === item.drop_locsub)) {
                opts.push({
                    value: item.drop_locsub,
                    label: this.packet.locsubsdict[item.drop_locsub] || item.drop_locsub
                });
            }
            return opts;
        },

        buildStageLocsubOptions: function(item) {
            let opts = [];
            if (item.item_type === 'transfer') {
                let allLocsubsdict = this.packet.locsubsdict || {};
                if (item.ship_locsub && allLocsubsdict[item.ship_locsub]) {
                    opts.push({ value: item.ship_locsub, label: allLocsubsdict[item.ship_locsub] });
                }
                if (item.drop_locsub && allLocsubsdict[item.drop_locsub]) {
                    opts.push({ value: item.drop_locsub, label: allLocsubsdict[item.drop_locsub] });
                }
                let defaultLocsub = "47|131";
                if (allLocsubsdict[defaultLocsub] && !opts.find(o => o.value === defaultLocsub)) {
                    opts.push({ value: defaultLocsub, label: allLocsubsdict[defaultLocsub] });
                }
                return opts.filter((option, index, self) =>
                    index === self.findIndex((t) => t.value === option.value)
                );
            }
            let allLocsubsdict = this.packet.locsubsdict || {};
            if (item.ship_locsub && allLocsubsdict[item.ship_locsub]) {
                opts.push({ value: item.ship_locsub, label: allLocsubsdict[item.ship_locsub] });
            }
            if (item.drop_locsub && allLocsubsdict[item.drop_locsub]) {
                opts.push({ value: item.drop_locsub, label: allLocsubsdict[item.drop_locsub] });
            }
            let defaultLocsub = item.order_type === "store_pickup" ? "47|162" : "47|131";
            if (allLocsubsdict[defaultLocsub] && !opts.find(o => o.value === defaultLocsub)) {
                opts.push({ value: defaultLocsub, label: allLocsubsdict[defaultLocsub] });
            }
            if (item.sold_locsub && allLocsubsdict[item.sold_locsub] && 
                !opts.find(o => o.value === item.sold_locsub)) {
                opts.push({ value: item.sold_locsub, label: allLocsubsdict[item.sold_locsub] });
            }
            return opts.filter((option, index, self) =>
                index === self.findIndex((t) => t.value === option.value)
            );
        },

        concater: function (items) {
            return items.join("|");
        },

        allowed: function (item) {
            let status = true;
            if (this.location_filter != null) {
                if (item.pick_location_id != this.location_filter) {
                    status = false;
                }
            }
            if (this.drop_location_filter != null) {
                if (item.ship_locsub && parseInt(item.ship_locsub.split("|")[0], 10) !== this.drop_location_filter) {
                    status = false;
                }
            }
            if (this.delivery_truck_filter != null) {
                if (item.truck != this.delivery_truck_filter) {
                    status = false;
                }
            }
            if (this.delivery_location_filter != null) {
                if (item.drop_locsub) {
                    const dropLocationId = parseInt(item.drop_locsub.split("|")[0], 10);
                    if (dropLocationId !== this.delivery_location_filter) {
                        status = false;
                    }
                } else {
                    status = false;
                }
            }
            if (this.pick_truck_filter != null) {
                if (item.pickup_truck != this.pick_truck_filter) {
                    status = false;
                }
            }
            if (item.quantity <= 0) {
                status = false;
            }
            return status;
        },

        search_dates: function(e) {
            if (e) e.preventDefault();
            if (!this.query.startDate || !this.query.endDate) {
                this.$bvToast.toast("Please select a date range.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                return;
            }
            this.get_pick_items_for_date();
        },

        get_pick_items_for_date: function () {
            let path = "/api/sales/get_pickup_items_WH_TRANSFER/"
            let data = {
                startDate: this.query.startDate,
                endDate: this.query.endDate,
                merchandise_status: [1],
                order_type: this.order_type,
                state: this.state ? Number(this.state) : null,
            }
            let that = this;
            let s = function (response) {
                if (response.data.result) {
                    that.items = response.data.result.map(item => {
                        that.originalValues[item.itemid] = {
                            pickup_truck: item.pickup_truck || null,
                            truck: item.truck || null,
                            drop_locsub: item.drop_locsub || null,
                            locsub: item.locsub || null,
                            ship_locsub: item.ship_locsub || null
                        };
                        return item;
                    });
                    that.update_trucks_stats();
                    that.get_batch_comments();
                    that.filterItems();
                    that.selectedItems = [];
                    that.selectAll = false;
                    that.commonTruck = null;
                    that.forceUpdateFlag = !that.forceUpdateFlag;
                } else {
                    console.log(response.data.error)
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
                that.items_being_loaded = false
            }
            let f = function (error) {
                console.log(error, "ees")
                that.$bvToast.toast("Error loading pickups for date range.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                that.items_being_loaded = false
            }
            this.items_being_loaded = true;
            Requestor.postSync(path, data, s, f);
        },

        isItemModified: function(item) {
            if (!this.originalValues[item.itemid]) {
                return true;
            }
            const original = this.originalValues[item.itemid];
            const hasPickupTruckChange = String(item.pickup_truck) !== String(original.pickup_truck);
            const hasDeliveryTruckChange = String(item.truck) !== String(original.truck);
            const hasDropLocsubChange = String(item.drop_locsub) !== String(original.drop_locsub);
            const hasLocsubChange = String(item.locsub) !== String(original.locsub);
            const hasShipLocsubChange = String(item.ship_locsub) !== String(original.ship_locsub);
            return hasPickupTruckChange || hasDeliveryTruckChange || 
                   hasDropLocsubChange || hasLocsubChange || hasShipLocsubChange;
        },

        onFieldChange: function(item) {
            this.forceUpdateFlag = !this.forceUpdateFlag;
            this.$forceUpdate();
        },

        onPickupTruckChange: function(item) {
            this.onFieldChange(item);
        },

        onDeliveryTruckChange: function(item) {
            this.$set(item, 'truck_modified', true);
            this.forceUpdateFlag = !this.forceUpdateFlag;
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        
        onDropLocsubChange: function(item) {
            this.checkAndSyncDeliveryStatus(item);
            this.onFieldChange(item);
        },

        onShipLocsubChange: function(item) {
            this.checkAndSyncDeliveryStatus(item);
            this.onFieldChange(item);
        },

        get_order_type_color: function (order_type, pickup_type, item_type) {
            if (item_type === 'transfer') {
                return '#ffd54f';
            }
            const type = (order_type || pickup_type || '').toUpperCase();
            if (type === 'STORE_PICKUP') return '#c5e1a5';
            else if (type === 'HOME_DELIVERY') return '#b3e5fc';
            else if (type === 'STORE_TRANSFER_ORDER') return '#ffd54f';
            else return '#efefef';
        },

        show_comments: function (item) {
            this.item_comments_invoice_id = this.getDisplayInvoiceId(item);
            this.item_comments_itemid = item.itemid;
            this.$bvModal.show("modal-comments-process-pickups");
        },

        save_pickup_item: function (item) {
            if (!this.isItemModified(item)) {
                this.$bvToast.toast("No changes to save for this item.", { title: "Info", autoHideDelay: 1500, variant: "info" });
                return;
            }
            let path = "/api/sales/update_invoice_items_process_pickups/"
            let data = {
                invoice_id: item.invoice_id,
                itemid: item.itemid,
                pickup_truck: item.pickup_truck,
                truck: item.truck,
                drop_locsub: item.drop_locsub,
                locsub: item.locsub,
                ship_locsub: item.ship_locsub,
                item_type: item.item_type || 'invoice',
            }
            if (item.item_type === 'transfer' && item.transfer_order_id) {
                data.transfer_order_id = item.transfer_order_id;
            }
            let that = this;
            let s = function (response) {
                if (!response.data.error) {
                    that.originalValues[item.itemid] = {
                        pickup_truck: item.pickup_truck,
                        truck: item.truck,
                        drop_locsub: item.drop_locsub,
                        locsub: item.locsub,
                        ship_locsub: item.ship_locsub
                    };
                    that.$bvToast.toast("Updated item's pickup/delivery details.", { title: "Success", autoHideDelay: 1500, variant: "success" });
                    that.forceUpdateFlag = !that.forceUpdateFlag;
                } else {
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            }
            let f = function (error) {
                that.$bvToast.toast("Error updating item's pickup/delivery details.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
            }
            Requestor.postAsync(path, data, s, f);
        },

        update_trucks_stats: function (item) {
            this.trucks_stats = {}
            for (var i = 0; i < this.items.length; i++) {
                let it = this.items[i];
                let truck = it.pickup_truck;
                if (truck in this.trucks_stats) {
                    this.trucks_stats[truck] += (parseInt(it.cubes) || 0);
                } else {
                    this.trucks_stats[truck] = (parseInt(it.cubes) || 0);
                }
            }
            for (var i = 0; i < this.trucks.length; i++) {
                this.trucks[i].cube_count = 0;
                this.trucks[i].cube_count = parseInt(this.trucks_stats[this.trucks[i].value]);
            }
            if (item) {
                this.save_pickup_item(item);
            }
            return;
        },

        update_selected_trucks_stats() {
            if (this.selectedItems.length > 1 && this.commonTruck) {
                this.selectedItems.forEach(selected => {
                    const index = this.items.findIndex(it => it.itemid === selected.itemid);
                    if (index === -1) return;
                    const current = this.items[index];
                    if (this.isDisabled(current) || current.state == 5) return;
                    if (!this.originalTrucks[selected.itemid]) {
                        this.originalTrucks[selected.itemid] = selected.pickup_truck;
                    }
                    selected.pickup_truck = this.commonTruck;
                    current.pickup_truck = this.commonTruck;
                });
                this.onFieldChange();
            }
        },

        save_selected_pickup_items: function() {
            let path = "/api/sales/update_invoice_items_process_pickups/";
            const savableItems = this.selectedItems.filter(item => {
                if (!this.isItemModified(item)) {
                    return false;
                }
                const original = this.originalValues[item.itemid];
                const hasDeliveryTruckChange = original && 
                    String(item.truck) !== String(original.truck);
                if (hasDeliveryTruckChange) {
                    const state = Number(item.state);
                    const allowedStatesForDeliveryTruck = [30, 11, 13, 9];
                    if (allowedStatesForDeliveryTruck.includes(state)) {
                        return true;
                    }
                }
                return !this.isDisabled(item);
            });

            if (savableItems.length === 0) {
                this.$bvToast.toast("No valid changes to save in selected items.", { title: "Info", autoHideDelay: 1500, variant: "info" });
                return;
            }

            let that = this;
            let savedCount = 0;
            let errorCount = 0;

            savableItems.forEach(item => {
                let data = {
                    invoice_id: item.invoice_id,
                    itemid: item.itemid,
                    pickup_truck: item.pickup_truck,
                    truck: item.truck,
                    drop_locsub: item.drop_locsub,
                    locsub: item.locsub,
                    ship_locsub: item.ship_locsub,
                    item_type: item.item_type || 'invoice',
                };
                if (item.item_type === 'transfer' && item.transfer_order_id) {
                    data.transfer_order_id = item.transfer_order_id;
                }
                let s = function (response) {
                    if (!response.data.error) {
                        that.originalValues[item.itemid] = {
                            pickup_truck: item.pickup_truck,
                            truck: item.truck,
                            drop_locsub: item.drop_locsub,
                            locsub: item.locsub,
                            ship_locsub: item.ship_locsub
                        };
                        savedCount++;
                        if (savedCount + errorCount === savableItems.length) {
                            that.$bvToast.toast(`Updated ${savedCount} of ${savableItems.length} item(s).`, { title: "Success", autoHideDelay: 1500, variant: "success" });
                            that.selectedItems = [];
                            that.selectAll = false;
                            that.commonTruck = null;
                            that.forceUpdateFlag = !that.forceUpdateFlag;
                        }
                    } else {
                        errorCount++;
                        that.$bvToast.toast(`Error updating item ${item.invoice_id}: ${response.data.error}`, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                };
                let f = function (error) {
                    errorCount++;
                    that.$bvToast.toast(`Error updating item ${item.invoice_id}`, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                };
                Requestor.postAsync(path, data, s, f);
            });
        },

        get_batch_comments: function () {
            let items = this.items.map(function (d) {
                return { 
                    invoice_id: d.invoice_id, 
                    itemid: d.itemid,
                    item_type: d.item_type || 'invoice',
                    transfer_order_id: d.transfer_order_id
                }
            })
            let path = "/api/sales/get_batch_comments/"
            let data = { items: items };
            let that = this;
            let s = function (response) {
                if (response.data.result) {
                    let comments = response.data.result;
                    if (comments.length > 0) {
                        that.comments_dict = Object.assign({}, ...comments.map((x) => ({ 
                            [x.item_type === 'transfer' ? 
                                `TRANSFER_${x.transfer_order_id}_${x.itemid}` : 
                                `${x.invoice_id}|${x.itemid}`]: x.n_comments 
                        })));
                    }
                } else {
                    console.log(response.data.error)
                    that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                }
            }
            let f = function (error) {
                that.$bvToast.toast("Error loading comments.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
            }
            Requestor.postAsync(path, data, s, f);
        },

        fetch_prereqs: function (order_type) {
            let that = this;
            let prereqs = [
                {
                    path: "/api/user/get_trucks/",
                    data: {},
                    type: "",
                    executable: Requestor.getAsync,
                    s: function (response) {
                        if (response.data.result) {
                            that.trucks = response.data.result.filter(item => item.userrole === "housetruck").map(function (d) {
                                return { value: d.id, text: d.username, cube_count: 0 }
                            });
                            that.trucks.forEach((el, index) => that.trucks_dict[el.value] = el.text);
                        } else {
                            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        }
                        that.trucks_being_loaded = false;
                    },
                    f: function (error) {
                        console.log(error)
                        that.$bvToast.toast("Error loading trucks.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                {
                    path: "/api/inventory/get_all_loc_inventory_dict/",
                    data: {},
                    type: "",
                    executable: Requestor.getAsync,
                    s: function (response) {
                        if (response.data.result) {
                            that.all_loc_inventory_dict = response.data.result;
                        } else {
                            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        }
                    },
                    f: function (error) {
                        console.log(error)
                        that.$bvToast.toast("Error loading inventory.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
                {
                    path: "/api/sales/get_default_locsubs/",
                    data: {},
                    type: "",
                    executable: Requestor.getAsync,
                    s: function (response) {
                        if (response.data.result.length > 0) {
                            that.default_locsubs = response.data.result;
                            that.drop_locsub = that.default_locsubs.filter(function (d) { return d.name == 'drop_locsub' })[0].locsub;
                            that.default_location_id = parseInt(that.drop_locsub.split("|")[0])
                        } else {
                            that.$bvToast.toast(response.data.error, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                        }
                    },
                    f: function (error) {
                        console.log(error)
                        that.$bvToast.toast("Error loading default locations.", { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                },
            ];
            for (var i = 0; i < prereqs.length; i++) {
                let path = prereqs[i]["path"];
                let data = prereqs[i]["data"];
                let s = prereqs[i]["s"];
                let f = prereqs[i]["f"];
                let type = prereqs[i]["type"];
                if (type == "secure") {
                    prereqs[i]["executable"](path, data, this, s, f);
                } else {
                    prereqs[i]["executable"](path, data, s, f);
                }
            }
        },

        sortby: function (key, mapper) {
            if (this.sort_asc) {
                this.items = Requestor.sort(this.items, key, mapper)
            } else {
                this.items = Requestor.sort_reverse(this.items, key, mapper)
            }
            this.sort_asc = !this.sort_asc;
        },

        get_csv_data: function () {
            let data = []
            let items = this.filteredTabItems;
            if (items.length > 0) {
                for (var i = 0; i < items.length; i++) {
                    let row = items[i];
                    data.push({
                        invoice_id: this.getDisplayInvoiceId(row),
                        product_name: row.product_name,
                        product_sku: row.product_sku,
                        quantity: row.quantity,
                        order_type: this.getDisplayOrderType(row),
                        pick_location: this.packet.locsubsdict[row.locsub],
                        drop_location: this.packet.locsubsdict[row.drop_locsub],
                        pickup_truck: this.packet.trucksdict[row.pickup_truck],
                        delivery_truck: this.packet.trucksdict[row.truck],
                        item_type: row.item_type || 'invoice',
                        transfer_status: this.getTruckStatus(row),
                        delivery_status: this.getDeliveryStatus(row)
                    })
                }
            }
            return data
        },

        get_csv_filename: function () {
            let startDate = this.query.startDate;
            let endDate = this.query.endDate;
            let tabName = this.activeTab === 0 ? 'PICKUP' : 'DELIVERY';
            return `${tabName}_PICKLIST_${startDate}_TO_${endDate}.csv`;
        },

        isDisabled: function (item) {
            if (item.item_type === 'transfer') {
                return false;
            }
            return item.locsub == '46|122' || Number(item.state) == 3 || Number(item.state) == 30 || Number(item.state) == 10 || Number(item.state) == 11 || Number(item.state) == 13;
        },

        isPickLocationDisabled: function (item) {
            if (item.item_type === 'transfer') {
                return false;
            }
            return Number(item.state) == 3 || Number(item.state) == 30 || Number(item.state) == 10 || Number(item.state) == 11 || Number(item.state) == 13;
        },

        toggleSelectAll() {
            if (this.selectAll) {
                this.selectedItems = this.filteredTabItems.filter(item => this.isSelectable(item));
            } else {
                this.selectedItems = [];
            }
            this.onFieldChange();
        },

        getAvailableLocsubs: function(item) {
            if (!item || !this.all_loc_inventory_dict) return [];
            return this.packet.locsubs.filter(locsub => {
                if (!locsub) return false;
                if (item.locsub === locsub.value) return true;
                const inventoryKey = this.concater([item.product_sku, locsub.location_id, locsub.sublocation_id]);
                const inventoryCount = this.all_loc_inventory_dict[inventoryKey] || 0;
                return inventoryCount > 0;
            });
        },

        isOutOfStock: function(item, locsub) {
            if (!item || !locsub || !this.all_loc_inventory_dict) return false;
            const inventoryKey = this.concater([item.product_sku, locsub.location_id, locsub.sublocation_id]);
            const inventoryCount = this.all_loc_inventory_dict[inventoryKey] || 0;
            return inventoryCount <= 0;
        },

        initDatePicker: function() {
            const Lightpick = require('lightpick');
            let that = this;
            var picker = new Lightpick({
                field: document.getElementById('dates'),
                singleDate: false,
                repick: true,
                format: 'YYYY-MM-DD',
                onSelect: function(start, end) {
                    that.query.startDate = start ? start.format('YYYY-MM-DD') : '';
                    that.query.endDate = end ? end.format('YYYY-MM-DD') : '';
                    if (start && end) {
                        that.get_pick_items_for_date();
                    }
                }
            });
            picker.setDate(moment().toDate());
        },

        toggleShowSameLocationItems: function() {
            this.showSameLocationItems = !this.showSameLocationItems;
            this.filterItems();
        },

        bulkAssignPickupTruck: function() {
            if (this.selectedItems.length === 0) {
                this.$bvToast.toast("Please select items to assign pickup truck.", { title: "Info", autoHideDelay: 1500, variant: "info" });
                return;
            }
            this.bulkAssignmentType = 'pickup';
            this.bulkAssignmentTitle = `Assign Pickup Truck to ${this.selectedPickupEligibleCount} Items`;
            this.bulkSelectedTruck = null;
            this.$bvModal.show('modal-bulk-assign-truck');
        },

        bulkAssignDeliveryTruck: function() {
            if (this.selectedItems.length === 0) {
                this.$bvToast.toast("Please select items to assign delivery truck.", { title: "Info", autoHideDelay: 1500, variant: "info" });
                return;
            }
            const assignableItems = this.selectedItems.filter(item => 
                this.shouldShowDeliveryTruck(item) && !this.isDeliveryTruckDisabled(item)
            );
            if (assignableItems.length === 0) {
                this.$bvToast.toast("Selected items don't require delivery trucks (same location) or are not eligible.", { title: "Info", autoHideDelay: 1500, variant: "info" });
                return;
            }
            this.bulkAssignmentType = 'delivery';
            this.bulkAssignmentTitle = `Assign Delivery Truck to ${assignableItems.length} Items`;
            this.bulkSelectedTruck = null;
            this.$bvModal.show('modal-bulk-assign-truck');
        },

        confirmBulkAssignment: function() {
            if (!this.bulkSelectedTruck || this.selectedItems.length === 0) {
                return;
            }
            let that = this;
            let updatedCount = 0;
            let errorCount = 0;

            const assignableItems = this.selectedItems.filter(item => {
                if (this.bulkAssignmentType === 'pickup') {
                    return !this.isDisabled(item) && item.state != 5;
                } else if (this.bulkAssignmentType === 'delivery') {
                    return this.shouldShowDeliveryTruck(item) && !this.isDeliveryTruckDisabled(item);
                }
                return false;
            });

            if (assignableItems.length === 0) {
                this.$bvToast.toast(`No ${this.bulkAssignmentType} assignable items in selection.`, { title: "Warning", autoHideDelay: 1500, variant: "warning" });
                this.$bvModal.hide('modal-bulk-assign-truck');
                return;
            }

            assignableItems.forEach(item => {
                if (this.bulkAssignmentType === 'pickup') {
                    item.pickup_truck = this.bulkSelectedTruck;
                    this.$set(item, 'pickup_truck_modified', true);
                } else if (this.bulkAssignmentType === 'delivery') {
                    item.truck = this.bulkSelectedTruck;
                    this.$set(item, 'truck_modified', true);
                }

                let data = {
                    invoice_id: item.invoice_id,
                    itemid: item.itemid,
                    pickup_truck: this.bulkAssignmentType === 'pickup' ? this.bulkSelectedTruck : item.pickup_truck,
                    truck: this.bulkAssignmentType === 'delivery' ? this.bulkSelectedTruck : item.truck,
                    drop_locsub: item.drop_locsub,
                    locsub: item.locsub,
                    ship_locsub: item.ship_locsub,
                    item_type: item.item_type || 'invoice',
                };

                if (item.item_type === 'transfer' && item.transfer_order_id) {
                    data.transfer_order_id = item.transfer_order_id;
                }

                let s = function (response) {
                    if (!response.data.error) {
                        that.originalValues[item.itemid] = {
                            pickup_truck: item.pickup_truck,
                            truck: item.truck,
                            drop_locsub: item.drop_locsub,
                            locsub: item.locsub,
                            ship_locsub: item.ship_locsub
                        };
                        updatedCount++;

                        if (updatedCount + errorCount === assignableItems.length) {
                            that.$bvToast.toast(
                                `${that.bulkAssignmentType === 'pickup' ? 'Pickup' : 'Delivery'} truck assigned to ${updatedCount} item(s).`,
                                { title: "Success", autoHideDelay: 1500, variant: "success" }
                            );
                            that.forceUpdateFlag = !that.forceUpdateFlag;
                            that.$bvModal.hide('modal-bulk-assign-truck');
                            that.bulkSelectedTruck = null;
                            that.selectedItems = [];
                            that.selectAll = false;
                        }
                    } else {
                        errorCount++;
                        that.$bvToast.toast(`Error updating item ${item.invoice_id}: ${response.data.error}`, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                    }
                };

                let f = function (error) {
                    errorCount++;
                    that.$bvToast.toast(`Error updating item ${item.invoice_id}`, { title: "Error", autoHideDelay: 1500, variant: "warning" });
                };

                Requestor.postAsync("/api/sales/update_invoice_items_process_pickups/", data, s, f);
            });
        },
    },

    computed: {
        truckTypeOptions() {
            return [
                { value: 'all', text: 'ALL TYPES' },
                { value: 'pickup', text: 'PICKUP TRUCK' },
                { value: 'delivery', text: 'DELIVERY TRUCK' }
            ];
        },
        
        deliveryLocationOptions() {
            const deliveryLocations = new Set();
            this.items.forEach(item => {
                if (item.drop_locsub) {
                    const locationId = item.drop_locsub.split('|')[0];
                    deliveryLocations.add(parseInt(locationId));
                }
            });
            return Array.from(deliveryLocations).map(locId => {
                let locationName = `Location ${locId}`;
                if (this.packet.locations) {
                    const location = this.packet.locations.find(loc => Number(loc.id) === Number(locId));
                    if (location && location.location) {
                        locationName = location.location;
                    }
                }
                if (this.packet.locsubsdict) {
                    for (const [key, value] of Object.entries(this.packet.locsubsdict)) {
                        if (key.startsWith(`${locId}|`)) {
                            const parts = value.split(' | ');
                            if (parts[0]) {
                                locationName = parts[0];
                                break;
                            }
                        }
                    }
                }
                return { value: parseInt(locId), text: locationName };
            }).sort((a, b) => a.text.localeCompare(b.text));
        },
        
        limitedStatusOptions() {
            const requiredStatuses = [0, 2, 3, 30, 14, 15, 11, 13, 9, 10];
            return mappings.states
                .filter(state => requiredStatuses.includes(state.value))
                .map(state => ({ value: state.value, text: state.text }));
        },
        
        selectAll: {
            get() {
                const selectableItems = this.filteredTabItems.filter(item => this.isSelectable(item));
                return selectableItems.length > 0 &&
                    this.selectedItems.length === selectableItems.length;
            },
            set(value) {
                if (value) {
                    this.selectedItems = this.filteredTabItems.filter(item => this.isSelectable(item));
                } else {
                    this.selectedItems = [];
                }
            }
        },
        
        // Items filtered by the currently active tab
        filteredTabItems() {
            return this.displayItems.filter(item => {
                if (this.activeTab === 0) {
                    return this.shouldShowInPickupTab(item);
                } else {
                    return this.shouldShowInDeliveryTab(item);
                }
            });
        },
        
        // Count for pickup tab badge
        pickupTabItemCount() {
            return this.items.filter(item => this.shouldShowInPickupTab(item)).length;
        },
        
        // Count for delivery tab badge
        deliveryTabItemCount() {
            return this.items.filter(item => this.shouldShowInDeliveryTab(item)).length;
        },
        
        // Count of selected items eligible for pickup truck assignment
        selectedPickupEligibleCount() {
            return this.selectedItems.filter(item => 
                !this.isDisabled(item) && item.state != 5
            ).length;
        },
        
        // Count of selected items eligible for delivery truck assignment
        selectedDeliveryEligibleCount() {
            return this.selectedItems.filter(item => 
                this.shouldShowDeliveryTruck(item) && !this.isDeliveryTruckDisabled(item)
            ).length;
        },
        
        displayItems() {
            const shouldApplyFilter = !this.showSameLocationItems;
            const hasActiveFilters = this.order_type !== null || 
                                    this.state !== null || 
                                    this.product_search_filter || 
                                    this.invoice_number_filter ||
                                    this.truck_assignment_filter !== 'all' || 
                                    this.delivery_status_filter !== 'all' ||
                                    this.pick_truck_filter !== null ||
                                    this.delivery_truck_filter !== null || 
                                    this.delivery_location_filter !== null || 
                                    this.truck_type_filter !== 'all' ||
                                    this.location_filter !== null ||
                                    this.drop_location_filter !== null;
            
            if (hasActiveFilters) {
                return this.filteredItems;
            }
            
            return this.items;
        },
        
        showSaveButton() {
            const flag = this.forceUpdateFlag;
            if (this.selectedItems.length === 0) {
                return false;
            }
            const savableItems = this.selectedItems.filter(item => {
                if (!this.isItemModified(item)) {
                    return false;
                }
                const original = this.originalValues[item.itemid];
                const hasDeliveryTruckChange = original && 
                    String(item.truck) !== String(original.truck);
                if (hasDeliveryTruckChange) {
                    const state = Number(item.state);
                    const allowedStatesForDeliveryTruck = [30, 11, 13, 9];
                    if (allowedStatesForDeliveryTruck.includes(state)) {
                        return true;
                    }
                }
                return !this.isDisabled(item);
            });
            return savableItems.length > 0;
        },
        
        saveButtonText() {
            if (this.selectedItems.length === 0) return "Save";
            const modifiedCount = this.selectedItems.filter(item =>
                this.isItemModified(item) && !this.isDisabled(item)
            ).length;
            if (modifiedCount === 1) return "Save Item";
            return `Save Items (${modifiedCount})`;
        },
        
        hasActiveTextFilters() {
            return this.product_search_filter || this.invoice_number_filter;
        },
        
        truckStatusOptions() {
            return [
                { value: 'not_assigned', text: 'NOT ASSIGNED' },
                { value: 'assigned', text: 'ASSIGNED' },
                { value: 'picked', text: 'PICKED' },
                { value: 'dropped', text: 'DROPPED' },
                { value: 'completed', text: 'COMPLETED' },
                { value: 'all', text: 'ALL STATUSES' }
            ];
        },
        
        deliveryStatusOptions() {
            return [
                { value: 'not_assigned', text: 'NOT ASSIGNED' },
                { value: 'assigned', text: 'ASSIGNED' },
                { value: 'picked', text: 'PICKED' },
                { value: 'dropped', text: 'DROPPED' },
                { value: 'completed', text: 'COMPLETED' },
                { value: 'all', text: 'ALL STATUSES' }
            ];
        },

        hasPickupAssignableItems() {
            return this.selectedItems.some(item => !this.isDisabled(item) && item.state != 5);
        },

        hasDeliveryAssignableItems() {
            return this.selectedItems.some(item => this.shouldShowDeliveryTruck(item) && !this.isDeliveryTruckDisabled(item));
        },
    },
    
    watch: {
        truck_type_filter() { this.filterItems(); },
        state() { this.filterItems(); },
        order_type() { this.filterItems(); },
        product_search_filter() { this.filterItems(); },
        invoice_number_filter() { this.filterItems(); },
        pick_truck_filter() { this.filterItems(); },
        delivery_truck_filter() { this.filterItems(); },
        truck_assignment_filter() { this.filterItems(); },
        delivery_status_filter() { this.filterItems(); },
        selectedItems(newVal) {
            this.forceUpdateFlag = !this.forceUpdateFlag;
        },
        items: {
            deep: true,
            handler(newItems) {
                newItems.forEach(item => {
                    if (!this.originalValues[item.itemid]) {
                        this.originalValues[item.itemid] = {
                            pickup_truck: item.pickup_truck,
                            truck: item.truck,
                            drop_locsub: item.drop_locsub,
                            locsub: item.locsub,
                            ship_locsub: item.ship_locsub
                        };
                    }
                });
                this.forceUpdateFlag = !this.forceUpdateFlag;
            }
        },
        delivery_location_filter() { this.filterItems(); },
        location_filter() { this.filterItems(); },
        drop_location_filter() { this.filterItems(); },
        showSameLocationItems() { this.filterItems(); },
    },
    
    mounted: function() {
        this.handleResize();
        window.addEventListener("resize", this.handleResize);
        this.initDatePicker();
        this.get_pick_items_for_date();
    },
    
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
    },
    
    created: function () {
        this.packet = JSON.parse(localStorage.packet);
        this.packet.userDetails = JSON.parse(localStorage.userDetails);
        this.packet.locsubsdict["-1|-1"] = "CUSTOMER HOME";
        
        const requiredStatuses = [0, 2, 3, 14, 15, 30, 11, 13];
        this.limitedStatesDict = {};
        mappings.states.forEach(state => {
            if (requiredStatuses.includes(state.value)) {
                this.limitedStatesDict[state.value] = state.text;
            }
        });
        
        this.fetch_prereqs();
    }
}
</script>

<style scoped>
.non-selectable {
    opacity: 0.6;
    background-color: #f5f5f5;
}
.ipt_tdr {
    font-family: roboto !important;
    font-size: 13px;
    background: white;
}

.pcol {
    padding: 5px;
}

.card-body {
    padding: 5px;
}

/* Truck status styles */
.truck-status-assigned {
    color: #28a745;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
}

.truck-status-not-assigned {
    color: #dc3545;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
}

.truck-status-delivered {
    color: #484f5c;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
}

.truck-status-picked {
    color: #ffae00;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
}

.truck-status-dropped {
    color: #17a2b8;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
}

/* Delivery status styles */
.delivery-status-delivered {
    color: #28a745;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid #c3e6cb;
    display: inline-block;
}

.delivery-status-in-picked {
    color: #ffc107;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid #ffeaa7;
    display: inline-block;
}

.delivery-status-dropped {
    color: #ffc107;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid #ffeaa7;
    display: inline-block;
}

.delivery-status-dispatched {
    color: #07b0ff;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid #ffeaa7;
    display: inline-block;
}

.delivery-status-assigned {
    color: #007bff;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid #b8daff;
    display: inline-block;
}

.delivery-status-not-assigned {
    color: #fc4545;
    font-weight: bold;
    background-color: #ffffff;
    padding: 2px 8px;
    border-radius: 4px;
    border: 1px solid #e9ecef;
    display: inline-block;
}

@media (max-width: 600px) {
    .responsive-input {
        width: 100% !important;
        display: block !important;
    }
    
    .filter-alert {
        margin-bottom: 5px !important;
        margin-right: 0 !important;
        width: 100% !important;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsProcessPickups.vue